import { useEffect, useState } from "react";
import { Box, Button, Container, Heading, Link, Stack, Text } from "@chakra-ui/react";
import { Link as ReactLink } from "react-router-dom";
import { useCmsApi } from "../../services/useCmsApi";

const DEFAULT_HERO_HEADING = "Take Control of Your Weeds";
const DEFAULT_HERO_TAGLINE = "Every farm has areas where weeds keep coming back. Those hot spots build deep seedbanks, spread year after year, and chip away at yields.";

const DEFAULT_HERO_IMAGE = "homepage-hero-field.png";

export const HeroSection = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [heading, setHeading] = useState(DEFAULT_HERO_HEADING);
	const [tagline, setTagline] = useState(DEFAULT_HERO_TAGLINE);
	const [heroImage, setHeroImage] = useState(DEFAULT_HERO_IMAGE);

	useEffect(() => {
		cmsApi.getSiteSetting("heroHeading").then((s) => { if (s?.value) setHeading(s.value); });
		cmsApi.getSiteSetting("heroTagline").then((s) => { if (s?.value) setTagline(s.value); });
		cmsApi.getSiteSetting("heroBackgroundImage").then((s) => { if (s?.value) setHeroImage(s.value); });
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
	<Box
		position="relative"
		bgImage={`url('${encodeURI(heroImage)}')`}
		bgSize="cover"
		bgPosition="center"
		mt={{ base: "-72px", lg: "-88px" }}
		pt={{ base: "calc(6rem + 72px)", md: "calc(8rem + 72px)", lg: "calc(10rem + 88px)" }}
		pb={{ base: "24", md: "40" }}
		overflow="hidden"
	>
		<Box
			position="absolute"
			inset={0}
			bgGradient="linear(to-r, blackAlpha.800, blackAlpha.550, blackAlpha.250)"
			zIndex={0}
		/>
		<Box
			position="absolute"
			inset={0}
			bgGradient="linear(to-b, blackAlpha.300, transparent, transparent)"
			zIndex={0}
		/>
		<Container maxW="1480px" position="relative" zIndex={1}>
			<Stack
				spacing="14"
				maxW="2xl"
				color="white"
			>
				<Stack spacing="4">
					<Heading
						size={{ base: "md", md: "4xl" }}
						fontWeight="extrabold"
						lineHeight="1.1"
						textShadow="0 2px 8px rgba(0,0,0,0.55)"
					>
						{heading}
					</Heading>
				</Stack>
				<Box
					display="inline-block"
					position="relative"
					maxW="lg"
					pl="0"
					pr={{ base: "4", md: "5" }}
					py={{ base: "3", md: "4" }}
					_before={{
						content: "\"\"",
						position: "absolute",
						top: "-20px",
						bottom: "-20px",
						left: "-168px",
						right: "-168px",
						background: "radial-gradient(ellipse closest-side at center, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.46) 52%, rgba(0,0,0,0.16) 78%, rgba(0,0,0,0) 100%)",
						pointerEvents: "none",
					}}
				>
					<Text
						position="relative"
						zIndex={1}
						fontSize={{ base: "lg", md: "xl" }}
						opacity={1}
						textShadow="0 2px 6px rgba(0,0,0,0.55)"
					>
						{tagline}
					</Text>
				</Box>
				<Text
					fontSize={{ base: "md", md: "lg" }}
					maxW="lg"
					opacity={0.95}
					fontStyle="italic"
					fontWeight="semibold"
					textShadow="0 2px 6px rgba(0,0,0,0.55)"
				>
					No new equipment. No new practices. Results in 1 day.
				</Text>
				<Stack direction={{ base: "column", sm: "row" }} spacing="4">
					<Link as={ReactLink} to="/example-farms" w={{ base: "200px", sm: "auto" }}>
						<Button
							w="full"
							size="lg"
							bg="green.600"
							color="white"
							_hover={{ bg: "green.700" }}
							fontWeight="bold"
						>
							See It In Use
						</Button>
					</Link>
					<Link as={ReactLink} to="/contact-us" w={{ base: "200px", sm: "auto" }}>
						<Button
							w="full"
							size="lg"
							bg="white"
							color="green.800"
							borderColor="white"
							_hover={{ bg: "gray.100" }}
						>
							Contact Us
						</Button>
					</Link>
				</Stack>
			</Stack>
		</Container>
	</Box>
	);
};
